import { lazy, type ReactNode } from "react";
import {
  DashboardOutlined, AppstoreOutlined, IdcardOutlined, TagsOutlined, BlockOutlined, ApiOutlined,
  PercentageOutlined, TeamOutlined, CreditCardOutlined, MobileOutlined, SafetyCertificateOutlined,
  HeartOutlined, FileTextOutlined, MessageOutlined, ShareAltOutlined, SettingOutlined, PartitionOutlined,
  BarChartOutlined, LineChartOutlined, RiseOutlined, TranslationOutlined, ShoppingOutlined, ShopOutlined, UnorderedListOutlined,
  ThunderboltOutlined, CodeOutlined, ClockCircleOutlined,
} from "@ant-design/icons";
// View registry: a tab's view key → its title (from preset filters) + page. Adding a real feature =
// swap its Placeholder for a lazy page. Pages read their own filters via useTabParams().
const DashboardPage = lazy(() => import("../../features/dashboard/DashboardPage.js"));
const OperationsPage = lazy(() => import("../../features/operations/OperationsPage.js"));
const CronPage = lazy(() => import("../../features/cron/CronPage.js"));
const ProvidersPage = lazy(() => import("../../features/providers/ProvidersPage.js"));
const ProviderTypesPage = lazy(() => import("../../features/providers/ProviderTypesPage.js"));
const AllTypesPage = lazy(() => import("../../features/providers/AllTypesPage.js"));
const ProviderTypeEditPage = lazy(() => import("../../features/providers/ProviderTypeEditPage.js"));
const BrandsPage = lazy(() => import("../../features/brands/BrandsPage.js"));
const BrandDetailPage = lazy(() => import("../../features/brands/BrandDetailPage.js"));
const BrandTypesPage = lazy(() => import("../../features/brand-types/BrandTypesPage.js"));
const DiscountProvidersPage = lazy(() => import("../../features/discounts/DiscountProvidersPage.js"));
const DiscountProviderEditPage = lazy(() => import("../../features/discounts/DiscountProviderEditPage.js"));
const DiscountProviderTypesPage = lazy(() => import("../../features/discount-provider-types/DiscountProviderTypesPage.js"));
const DiscountProviderTypeEditPage = lazy(() => import("../../features/discount-provider-types/DiscountProviderTypeEditPage.js"));
const DiscountsPage = lazy(() => import("../../features/discounts/DiscountsPage.js"));
const DiscountEditPage = lazy(() => import("../../features/discounts/DiscountEditPage.js"));
const PromotionsPage = lazy(() => import("../../features/promotions/PromotionsPage.js"));
const UsersPage = lazy(() => import("../../features/users/UsersPage.js"));
const UserEditPage = lazy(() => import("../../features/users/UserEditPage.js"));
const UserCardsPage = lazy(() => import("../../features/users/UserCardsPage.js"));
const CardsPage = lazy(() => import("../../features/cards/CardsPage.js"));
const SharedCardsPage = lazy(() => import("../../features/cards/SharedCardsPage.js"));
const CardDetailPage = lazy(() => import("../../features/cards/CardDetailPage.js"));
const BalanceRunnerPage = lazy(() => import("../../features/balance/BalanceRunnerPage.js"));
const WorkersPage = lazy(() => import("../../features/workers/WorkersPage.js"));
const HealthPage = lazy(() => import("../../features/health/HealthPage.js"));
const LogsPage = lazy(() => import("../../features/logs/LogsPage.js"));
const SupportPage = lazy(() => import("../../features/support/SupportPage.js"));
const SupportDetailPage = lazy(() => import("../../features/support/SupportDetailPage.js"));
const SettingsPage = lazy(() => import("../../features/settings/SettingsPage.js"));
const RegulatedPage = lazy(() => import("../../features/regulated/RegulatedPage.js"));
const PricesPage = lazy(() => import("../../features/prices/PricesPage.js"));
const ManufacturersPage = lazy(() => import("../../features/manufacturers/ManufacturersPage.js"));
const ProducePage = lazy(() => import("../../features/prices/ProducePage.js"));
const ProviderItemsPage = lazy(() => import("../../features/prices/ProviderItemsPage.js"));
const ProviderPromotionsPage = lazy(() => import("../../features/prices/ProviderPromotionsPage.js"));
const ProviderStoresPage = lazy(() => import("../../features/prices/ProviderStoresPage.js"));
const StoreDetailPage = lazy(() => import("../../features/prices/StoreDetailPage.js"));
const ProviderItemPage = lazy(() => import("../../features/prices/ProviderItemPage.js"));
const PriceRecordsPage = lazy(() => import("../../features/prices/PriceRecordsPage.js"));
const PriceRecordPage = lazy(() => import("../../features/prices/PriceRecordPage.js"));
const PriceHistoryPage = lazy(() => import("../../features/prices/PriceHistoryPage.js"));
const ProviderPromotionPage = lazy(() => import("../../features/prices/ProviderPromotionPage.js"));
const StatsPage = lazy(() => import("../../features/stats/StatsPage.js"));
const TranslationsPage = lazy(() => import("../../features/translations/TranslationsPage.js"));
const TranslationEditPage = lazy(() => import("../../features/translations/TranslationEditPage.js"));
const EventsPage = lazy(() => import("../../features/events/EventsPage.js"));

export type ViewDef = { title: (p: URLSearchParams) => string; element: ReactNode };

export const VIEWS: Record<string, ViewDef> = {
  dashboard: { title: () => "Dashboard", element: <DashboardPage /> },
  operations: { title: () => "Operations", element: <OperationsPage /> },
  cron: { title: () => "Cron", element: <CronPage /> },
  statistics: { title: () => "Statistics", element: <StatsPage /> },
  providers: { title: () => "Providers", element: <ProvidersPage /> },
  "provider-types": { title: (p) => `Types · ${p.get("provider") ?? ""}`, element: <ProviderTypesPage /> }, // per-provider (row cross-link)
  "card-types": { title: () => "Provider Types", element: <AllTypesPage /> },
  "provider-type": { title: () => "Edit provider type", element: <ProviderTypeEditPage /> },
  brands: { title: (p) => (p.get("provider") ? `Brands · ${p.get("provider")}` : p.get("status") ? `Brands · ${p.get("status")}` : "Brands"), element: <BrandsPage /> },
  brand: { title: () => "Brand", element: <BrandDetailPage /> },
  "brand-types": { title: () => "Brand Types", element: <BrandTypesPage /> },
  "discount-providers": { title: () => "Discount Providers", element: <DiscountProvidersPage /> },
  "discount-provider": { title: (p) => (p.get("code") ? `Provider · ${p.get("code")}` : "New provider"), element: <DiscountProviderEditPage /> },
  "discount-provider-types": { title: () => "Discount Categories", element: <DiscountProviderTypesPage /> },
  "discount-category": { title: (p) => (p.get("id") ? "Edit category" : "New category"), element: <DiscountProviderTypeEditPage /> },
  discounts: { title: (p) => (p.get("provider") ? `Discounts · ${p.get("provider")}` : "Discounts"), element: <DiscountsPage /> },
  discount: { title: () => "Discount", element: <DiscountEditPage /> },
  promotions: { title: () => "Promotions", element: <PromotionsPage /> },
  users: { title: () => "Users", element: <UsersPage /> },
  user: { title: () => "User", element: <UserEditPage /> },
  "user-cards": { title: (p) => `Cards · ${p.get("user")?.slice(0, 6) ?? ""}`, element: <UserCardsPage /> },
  cards: { title: () => "Cards", element: <CardsPage /> },
  "shared-cards": { title: () => "Shared cards", element: <SharedCardsPage /> },
  card: { title: () => "Card", element: <CardDetailPage /> },
  "balance-runner": { title: (p) => (p.get("card") ? "Balance Runner · card" : "Balance Runner"), element: <BalanceRunnerPage /> },
  workers: { title: () => "Workers", element: <WorkersPage /> },
  health: { title: () => "Health", element: <HealthPage /> },
  logs: { title: () => "Logs", element: <LogsPage /> },
  support: { title: () => "Support", element: <SupportPage /> },
  "support-detail": { title: (p) => `Ticket · ${p.get("id")?.slice(0, 8) ?? ""}`, element: <SupportDetailPage /> },
  settings: { title: () => "Settings", element: <SettingsPage /> },
  translations: { title: () => "Translations", element: <TranslationsPage /> },
  translation: { title: (p) => (p.get("key") ? `String · ${p.get("key")}` : "String"), element: <TranslationEditPage /> },
  events: { title: () => "Events", element: <EventsPage /> },
  regulated: { title: () => "Gov prices", element: <RegulatedPage /> },
  prices: { title: () => "Price Providers", element: <PricesPage /> },
  "price-items": { title: (p) => (p.get("name") ? `Items · ${p.get("name")}` : "Items"), element: <ProviderItemsPage /> },
  "price-promos": { title: (p) => (p.get("name") ? `Promotions · ${p.get("name")}` : "Promotions"), element: <ProviderPromotionsPage /> },
  "price-stores": { title: (p) => (p.get("name") ? `Stores · ${p.get("name")}` : "Stores"), element: <ProviderStoresPage /> },
  "price-store": { title: (p) => (p.get("name") ? `Store · ${p.get("name")}` : "Store"), element: <StoreDetailPage /> },
  "price-item": { title: (p) => p.get("name") ?? "Product", element: <ProviderItemPage /> },
  "price-promo": { title: (p) => p.get("name") ?? "Promotion", element: <ProviderPromotionPage /> },
  "price-records": { title: (p) => (p.get("name") ? `Prices · ${p.get("name")}` : "Prices"), element: <PriceRecordsPage /> },
  "price-record": { title: (p) => (p.get("name") ? `Price · ${p.get("name")}` : "Price record"), element: <PriceRecordPage /> },
  "price-history": { title: (p) => (p.get("name") ? `History · ${p.get("name")}` : "Price history"), element: <PriceHistoryPage /> },
  manufacturers: { title: () => "Manufacturers", element: <ManufacturersPage /> },
  products: { title: () => "Produce", element: <ProducePage /> },
};

export const DEFAULT_VIEW = "dashboard";

/** Sidebar menu — leaf `key`s must exist in VIEWS; group keys (grp-*) are just collapsible headers. */
type MenuNode = { key: string; label: string; icon?: ReactNode; children?: MenuNode[] };
export const MENU: MenuNode[] = [
  { key: "dashboard", label: "Dashboard", icon: <DashboardOutlined /> },
  { key: "operations", label: "Operations", icon: <ThunderboltOutlined /> }, // all scan/sync/GC ops (moved off the Dashboard)
  { key: "cron", label: "Cron", icon: <ClockCircleOutlined /> }, // scheduler manager: run/cancel jobs, history+log, config, DB-load

  { key: "promotions", label: "Promotions", icon: <RiseOutlined /> }, // moved up: sits under Dashboard
  { key: "statistics", label: "Statistics", icon: <BarChartOutlined /> },
  { key: "grp-giftcards", label: "Gift Cards", icon: <IdcardOutlined />, children: [
    { key: "providers", label: "Providers", icon: <AppstoreOutlined /> },
    { key: "card-types", label: "Types", icon: <IdcardOutlined /> },
    { key: "cards", label: "Cards", icon: <CreditCardOutlined /> },
    { key: "shared-cards", label: "Shared cards", icon: <ShareAltOutlined /> },
    { key: "balance-runner", label: "Balance Runner", icon: <MobileOutlined /> },
  ] },
  { key: "grp-brands", label: "Brands", icon: <TagsOutlined />, children: [
    { key: "brands", label: "Brands", icon: <TagsOutlined /> },
    { key: "brand-types", label: "Brand Types", icon: <BlockOutlined /> },
  ] },
  { key: "grp-discounts", label: "Discounts", icon: <PercentageOutlined />, children: [
    { key: "discount-providers", label: "Providers", icon: <ApiOutlined /> },
    { key: "discount-provider-types", label: "Categories", icon: <PartitionOutlined /> },
    { key: "discounts", label: "Discounts", icon: <PercentageOutlined /> },
  ] },
  { key: "grp-prices", label: "Prices", icon: <ShopOutlined />, children: [
    { key: "prices", label: "Providers", icon: <ShopOutlined /> },
    { key: "price-stores", label: "Stores", icon: <ShopOutlined /> },
    { key: "price-items", label: "Items", icon: <UnorderedListOutlined /> },
    { key: "manufacturers", label: "Manufacturers", icon: <TagsOutlined /> },
    { key: "products", label: "Produce", icon: <ShoppingOutlined /> },
    { key: "price-promos", label: "Item Promotions", icon: <TagsOutlined /> },
    { key: "price-records", label: "Prices", icon: <BarChartOutlined /> },
    { key: "price-history", label: "History", icon: <LineChartOutlined /> },
    { key: "regulated", label: "Gov prices", icon: <ShoppingOutlined /> }, // moved into Prices submenu
  ] },
  { key: "grp-admin", label: "Administration", icon: <SafetyCertificateOutlined />, children: [
    { key: "users", label: "Users", icon: <TeamOutlined /> },
    { key: "workers", label: "Workers", icon: <SafetyCertificateOutlined /> },
    { key: "logs", label: "Logs", icon: <FileTextOutlined /> },
    { key: "support", label: "Support", icon: <MessageOutlined /> },
    { key: "settings", label: "Settings", icon: <SettingOutlined /> },
    { key: "health", label: "Health", icon: <HeartOutlined /> },
    { key: "translations", label: "Translations", icon: <TranslationOutlined /> },
    // The shared coded-string enum (error codes, status-change reasons) — sits by Translations, which
    // holds the user-facing wording for the same event ids.
    { key: "events", label: "Events", icon: <CodeOutlined /> },
  ] },
];
